import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import { Typography } from "@material-tailwind/react";
import { FullScreenLoading } from "components/fullScreenLoading";
import DialogConfirm from "components/wps/DialogConfirm";
import Pagination from "components/wps/Pagination";
import moment from "moment";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import ApiService from "utills/ApiService";
import { SwitchButton } from "components/switchButton";
import './dashboard.scss';
import { isHaveActionPermission } from "utills/helpers";

const Dashboard = (props: any) => {
  const { isMobile, setFilter, translate, filter, search, applyDate, page, setPage, token, permissions } = props;
  const [paging, setPaging] = useState(null as any);
  const [accounts, setAccounts] = useState([] as any);
  const TABLE_HEAD = [translate('no'), translate('full-name'), "Email", translate('role-name'), translate('created-at'), translate('created-by'), translate('status'), translate('action')];
  const [open, setOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [activeAccount, setActiveAccount] = useState({
    is_active: false,
    user_id: ''
  });
  const [submitAction, setSubmitAction] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token !== '' && filter) {
      fetchAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, token]);

  const fetchAccount = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`account/page/${page}?q=${search.q}&gte=${applyDate.start}&lte=${applyDate.end}&is_active=${search.status}&role_id=${search.role_id}&limit=10`, token);

      if (result) {
        setPaging(result.paging);
        setAccounts(result.users);
        setFilter(false);
      }
    } catch (error: any) {
      // toast.error(error.message);
    }

    setLoading(false);
  }

  const handleConfirmAction = async (is_active: boolean, user_id: string) => {
    setOpen(true);

    is_active ? setDialogMessage(translate('confirm-active-account')) : setDialogMessage(translate('confirm-inactive-account'));
    const activeData = {
      user_id,
      is_active
    };

    setActiveAccount(activeData);
  };

  useEffect(() => {
    if (token !== '' && submitAction) {
      handleActiveAccount();
    }
  }, [submitAction, activeAccount, translate, accounts, token]);

  const handleActiveAccount = async () => {
    try {
      const result = await ApiService.put(`account/${activeAccount.user_id}/active/${activeAccount.is_active}`, activeAccount, token);

      if (result) {
        setSubmitAction(false);
        const account_temp = [...accounts];
        account_temp.forEach((item: any) => {
          if (item.id === activeAccount.user_id) {
            item.is_active = activeAccount.is_active;
          }
        });
        setAccounts([...account_temp]);
        toast.success(translate('update-success'));
      }
    } catch (error: any) {
      toast.error(error.message);
      setSubmitAction(false);
    }
  }

  if (!isMobile) {
    return (
      <div className="w-full p-[16px] bg-white rounded-[8px]" id="dashboard-account-list">
        <div className="h-full w-full overflow-x-auto overflow-y-hidden">
          <table className="w-full min-w-max table-auto text-left">
            <thead className="h-[46px] bg-[#f6f5fe]">
              <tr>
                {TABLE_HEAD.map((head, i) => (
                  <th
                    key={head}
                    className="content-table"
                  >
                    <Typography
                      {...{} as any}
                      variant="small"
                      className="!text-14 !font-500 px-[8px]"
                    >
                      {head}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {
                accounts.length > 0 &&
                accounts.map((item: any, index: number) => {
                  const isLast = index === accounts.length - 1;
                  const classes = isLast ? "!text-14 px-[8px] h-[46px]" : "border-b border-blue-gray-50 !text-14 px-[8px] h-[46px]";

                  return (
                    <tr key={`account_${item.id}_${index}`} className='table-row'>
                      <td className={classes}>
                        <Typography
                          {...{} as any}
                          variant="small"
                        >
                          {
                            index < 9 &&
                            <span>{paging.current_page - 1}{index + 1}</span>
                          }

                          {
                            index === 9 &&
                            <span>{paging.current_page}0</span>
                          }
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          {...{} as any}
                          variant="small"
                        >
                          {item.first_name} {item.last_name}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          {...{} as any}
                          variant="small"
                        >
                          {item.email}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          {...{} as any}
                          variant="small"
                        >
                          {item.role_name}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          {...{} as any}
                          variant="small"
                        >
                          {moment(item.created).format('DD/MM/YYYY')}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          {...{} as any}
                          variant="small"
                        >
                          {item.created_by}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          {...{} as any}
                          variant="small"
                        >
                          <SwitchButton checked={item.is_active}
                            onChange={() => handleConfirmAction(!item.is_active, item.id)} disabled={((item.role_name.toLowerCase() === 'owner') ? true : false) || !isHaveActionPermission("account", "organization", "edit", permissions)} />
                        </Typography>
                      </td>
                      <td className={classes}>
                        <div className="w-full flex items-center gap-x-[4px]">
                          <a href={`/organization/account/${item.id}`}>
                            <div className="size-[36px] rounded-[8px] flex items-center justify-center bg-[#f5f6f7]">
                              <EyeIcon className="size-[16px] text-black" strokeWidth={3} />
                            </div>
                          </a>
                          {
                            (item.role_name.toLowerCase() !== 'owner') &&
                            <>
                              {isHaveActionPermission(
                                "account",
                                "organization",
                                "edit",
                                permissions
                              ) && (
                                <a
                                  href={`/organization/account/${item.id}/edit`}
                                >
                                  <div className="size-[36px] rounded-[8px] flex items-center justify-center bg-[#f5f6f7]">
                                    <PencilIcon
                                      className="size-[16px] text-black"
                                      strokeWidth={3}
                                    />
                                  </div>
                                </a>
                              )}
                            </>
                          }

                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {
            paging && paging.total_page > 1 &&
            <div id="pagination-desktop">
              <Pagination
                setPage={setPage}
                paging={paging}
                setFilter={setFilter}
              />
            </div>
          }
        </div>

        <DialogConfirm
          open={open}
          isMobile={isMobile}
          setOpen={setOpen}
          setSubmitAction={setSubmitAction}
          dialogMessage={dialogMessage}
        />

        {
          loading &&
          <FullScreenLoading />
        }
      </div>
    )
  } else {
    return (
      <div className="w-full grid grid-cols-1 gap-[16px]">
        {
          accounts.length > 0 && accounts.map((item: any, index: number) => {
            return (
              <div className="bg-white p-[16px] w-full rounded-[8px] grid grid-cols-1 gap-[12px]" key={`account_${item.email}_${index}`}>
                <div className="w-full flex items-center">
                  <SwitchButton checked={item.is_active}
                    onChange={() => handleConfirmAction(!item.is_active, item.id)} disabled={((item.role_name.toLowerCase() === 'owner') ? true : false) || !isHaveActionPermission("account", "organization", "edit", permissions)} />

                  <div className="flex-1 flex items-center gap-x-[8px] justify-end">
                    <a href={`/organization/account/${item.id}`}>
                      <div className="size-[35px] rounded-[4px] flex items-center justify-center bg-[#f5f6f7]">
                        <EyeIcon className="size-[16px] text-black" strokeWidth={3} />
                      </div>
                    </a>

                    {
                      (item.role_name.toLowerCase() !== 'owner') &&
                      <>
                        {isHaveActionPermission(
                          "account",
                          "organization",
                          "edit",
                          permissions
                        ) && (
                          <a href={`/organization/account/${item.id}/edit`}>
                            <div className="size-[35px] rounded-[4px] flex items-center justify-center bg-[#f5f6f7]">
                              <PencilIcon
                                className="size-[16px] text-black"
                                strokeWidth={3}
                              />
                            </div>
                          </a>
                        )}
                      </>
                    }

                  </div>
                </div>

                <div className="w-full flex items-center gap-[12px]">
                  <div className="w-[130px] text-14 font-500">{translate('no')}</div>
                  <div className="account-info-value">
                    {
                      index < 9 &&
                      <span>{paging.current_page - 1}{index + 1}</span>
                    }

                    {
                      index === 9 &&
                      <span>{paging.current_page}0</span>
                    }
                  </div>
                </div>

                <div className="w-full flex items-center gap-[12px]">
                  <div className="w-[130px] text-14 font-500">{translate('full-name')}</div>
                  <div className="text-14 font-400">{item.first_name} {item.last_name}</div>
                </div>

                <div className="w-full flex items-center gap-[12px]">
                  <div className="w-[130px] text-14 font-500">Email</div>
                  <div className="text-14 font-400">{item.email}</div>
                </div>

                <div className="w-full flex items-center gap-[12px]">
                  <div className="w-[130px] text-14 font-500">{translate('role-name')}</div>
                  <div className="text-14 font-400">{item.type}</div>
                </div>

                <div className="w-full flex items-center gap-[12px]">
                  <div className="w-[130px] text-14 font-500">{translate('created-at')}</div>
                  <div className="text-14 font-400">{moment(item.created).format('DD/MM/YYYY')}</div>
                </div>

                <div className="w-full flex items-center gap-[12px]">
                  <div className="w-[130px] text-14 font-500">{translate('created-by')}</div>
                  <div className="text-14 font-400">{item.created_by}</div>
                </div>
              </div>
            )
          })
        }

        {
          paging && paging.total_page > 1 &&
          <Pagination
            setPage={setPage}
            paging={paging}
            setFilter={setFilter}
          />
        }

        <DialogConfirm
          open={open}
          isMobile={isMobile}
          setOpen={setOpen}
          setSubmitAction={setSubmitAction}
          dialogMessage={dialogMessage}
        />

        {
          loading &&
          <FullScreenLoading />
        }
      </div>
    )
  }

}

export default Dashboard;